import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Input, Switch, Tooltip } from 'antd'
import styled from 'styled-components'

import * as Rules from 'admin/lib/ValidationRules'
import { DualTimeZoneDateTimePicker } from 'admin/pages/RecordEdit/common/DualTimeZoneDateTimePicker'
import {
  AutoCompleteFormItem,
  FormItem,
  FormRowWithValidation,
  FormSectionTitle,
  FormSpace,
  SectionProps,
  UnstyledSection,
  UnstyledSectionWrapper
} from 'admin/lib/Forms'
import { Flag } from 'admin/lib/Icons/Flag'

import { usDateFormatter } from 'admin/lib/Functions/timeZoneHelper'
import { Waste as fields } from 'admin/lib/Records/fields'

import { CarrierPresetSelect } from 'admin/pages/RecordEdit/Waste/CarrierPresetSelect'
import { FacilityPresetSelect } from 'admin/pages/RecordEdit/Waste/FacilityPresetSelect'
import { useDateAutoCopy } from 'admin/pages/RecordEdit/common/useDateAutoCopy'
import { KnownAddressInput } from 'admin/pages/RecordEdit/common/KnownAddressInput'
import {
  RAW,
  ENRICHED,
  ENRICHED_DISABLED_MESSAGE
} from 'admin/pages/RecordEdit/common/constants'
import {
  isDisabled,
  isHidden
} from 'admin/pages/RecordEdit/common/rawEnrichedHelpers'
import {
  TitleBar,
  ShowRawWrapper,
  DataToggle,
  DataToggleItem
} from 'admin/pages/RecordEdit/common/styledComponents'

import {
  CheckCarrierLicence,
  CheckCarrierName,
  CheckConsignmentOrDeliveryNumber,
  CheckReceiverOrSupplierOrSenderOrCarrierReference,
  CheckSubContractor,
  CheckVehicleReg,
  CheckWastePermit,
  CheckWasteSICCode
} from 'admin/lib/SimpleDataValidation'
import {
  calculateWarningsForArrivalDate,
  calculateWarningsForExitDate
} from 'admin/pages/RecordEdit/common/dateValidation'

type Props = {
  createdAt: string
  projectName: string
  projectCountry: string
  projectTimeZone: string
  projectId: string
  isImperial: boolean
  accountId: string
  dataView: string
  showRaw: boolean
  enrichedDisabled: boolean
  setDataView: (v: string) => void
  setShowRaw: () => void
  setEnrichedDisabled: (v: boolean) => void
  enrichedSourceTracking: any
} & Omit<SectionProps, 'children'>

export const WasteData = (props: Props) => {
  const [removalDateWarnings, setRemovalDateWarnings] = useState<string[]>([])
  const [removalExitDateWarnings, setRemovalExitDateWarnings] = useState<
    string[]
  >([])

  const form = useFormContext()
  const dirtyFields = form.formState.dirtyFields

  // Register non-interactive fields set from setValue
  useEffect(() => {
    form.register(fields.enriched.originSiteAddressEntered.path)
    form.register(fields.enriched.originSiteAddressCoordinates.path)
    form.register(fields.enriched.facilitySiteAddressEntered.path)
    form.register(fields.enriched.facilitySiteAddressCoordinates.path)
  }, [form])

  // Using 'watch' to listen for changes and trigger redraw (getValues does not trigger a redraw)
  const removalDate = form.watch('enriched.removalDate')
  const journeyDistanceMiles = form.watch('enriched.journey.distanceMiles')

  const enrichedSourceTracking = useMemo(() => {
    if (
      props.enrichedSourceTracking?.facilityPermitId?.transformerDetails ===
        'EADataMatch' ||
      props.enrichedSourceTracking?.facilityOperatorName?.transformerDetails ===
        'EADataMatch' ||
      props.enrichedSourceTracking?.facilitySiteAddress?.transformerDetails ===
        'EADataMatch'
    ) {
      return {
        type: 'info',
        message: 'Match found with EA/SEPA/NRW data'
      }
    } else if (
      props.enrichedSourceTracking?.facilityPermitId?.transformerDetails?.startsWith(
        'GapFill'
      )
    ) {
      return {
        type: 'info',
        message: 'Facility populated by gap filling'
      }
    }
    return {
      type: 'warning',
      message:
        'The Data Transformer was unable to make a match with EA/SEPA/NRW data'
    }
  }, [props.enrichedSourceTracking])

  const exitDatePopulation = useDateAutoCopy(
    fields.enriched.removalDate.path,
    fields.enriched.removalExitDate.path
  )

  const validateArrivalDate = (value: any) => {
    setRemovalDateWarnings(
      calculateWarningsForArrivalDate(
        value,
        props.createdAt,
        props.projectTimeZone
      )
    )
    // Trigger Exit date validation too:
    validateExitDate(form.getValues(fields.enriched.removalExitDate.path))
    // Always return TRUE so we do not block form submission
    return true
  }

  const validateExitDate = (value: any) => {
    setRemovalExitDateWarnings(
      calculateWarningsForExitDate(
        value,
        removalDate,
        props.createdAt,
        props.projectTimeZone
      )
    )
    // Always return TRUE so we do not block form submission
    return true
  }

  useEffect(() => {
    let sub1: { unsubscribe: () => void }, sub2: { unsubscribe: () => void }
    if (props.isImperial) {
      sub1 = form.watch('enriched.removalDate')
      form.setValue(
        'enriched.removalDateUS',
        usDateFormatter(form.getValues('enriched.removalDate'))
      )

      sub2 = form.watch('enriched.removalExitDate')
      form.setValue(
        'enriched.removalExitDateUS',
        usDateFormatter(form.getValues('enriched.removalExitDate'))
      )
    }
    return () => {
      if (sub1 && sub1.unsubscribe) {
        sub1.unsubscribe()
      }
      if (sub2 && sub2.unsubscribe) {
        sub2.unsubscribe()
      }
    }
  }, [form, props.isImperial])

  const handleCarrierSelectChange = () => {
    // When select value changes trigger re-validation of Carrier Name & Licence
    document
      .getElementById('wasteDataCarrierName')
      ?.dispatchEvent(new Event('click', { bubbles: true }))
    document
      .getElementById('wasteDataCarrierLicence')
      ?.dispatchEvent(new Event('click', { bubbles: true }))

    // Return focus to Carrier Name
    document
      .getElementById('wasteDataCarrierName')
      ?.dispatchEvent(new Event('click', { bubbles: true }))
  }

  const handleFacilitySelectChange = () => {
    // When select value changes trigger re-validation of Facility Address and Permit
    document
      .getElementById('wasteDataFacilityPermit')
      ?.dispatchEvent(new Event('click', { bubbles: true }))

    const address = document.getElementById('wasteDataFacilitySiteAddress')
    address?.focus()
    address?.blur()

    // Return focus to Facility Operator Name
    document
      .getElementById('wasteDataFacilityOperatorName')
      ?.dispatchEvent(new Event('click', { bubbles: true }))
  }

  useEffect(() => {
    if (dirtyFields && dirtyFields.raw) {
      props.setEnrichedDisabled(true)
    }
  }, [dirtyFields, props])

  return (
    <UnstyledSectionWrapper className="UnstyledSectionWrapper">
      <UnstyledSection {...props}>
        {({ firstInputRef }) => (
          <>
            <TitleBar>
              <FormSectionTitle level={5}>
                <Flag country={props.projectCountry} /> Waste
              </FormSectionTitle>
              {props.dataView === ENRICHED && (
                <ShowRawWrapper>
                  <Switch
                    checked={props.showRaw}
                    onChange={props.setShowRaw}
                    size="small"
                  />
                  <div style={{ marginTop: '1px' }}>Show Raw</div>
                </ShowRawWrapper>
              )}
              <DataToggle>
                <DataToggleItem
                  className={props.dataView === RAW ? 'active' : ''}
                  onClick={() => {
                    performance.mark('raw-enriched-tab-switch', {
                      detail: { context: 'waste', destination: RAW }
                    })
                    props.setDataView(RAW)
                  }}
                >
                  Raw
                </DataToggleItem>
                <DataToggleItem
                  className={
                    props.dataView === ENRICHED
                      ? 'active'
                      : props.enrichedDisabled
                        ? 'disabled'
                        : ''
                  }
                  onClick={() => {
                    if (!props.enrichedDisabled) {
                      performance.mark('raw-enriched-tab-switch', {
                        detail: { context: 'waste', destination: ENRICHED }
                      })
                      props.setDataView(ENRICHED)
                    }
                  }}
                >
                  <Tooltip
                    title={
                      props.enrichedDisabled ? ENRICHED_DISABLED_MESSAGE : ''
                    }
                    placement="right"
                    mouseLeaveDelay={0.5}
                  >
                    Enriched
                  </Tooltip>
                </DataToggleItem>
              </DataToggle>
            </TitleBar>
            <FormSpace />

            <FormRowWithValidation>
              <FormItem
                label={fields.raw.removalDate.label}
                name={fields.raw.removalDate.path}
                disabled={isDisabled(
                  fields.raw.removalDate.isRawField,
                  props.dataView,
                  props.showRaw
                )}
                hidden={isHidden(
                  fields.raw.removalDate.isRawField,
                  props.dataView,
                  props.showRaw
                )}
                inputRef={props.dataView === RAW ? firstInputRef : null}
                instrumented={true}
              />

              <FormItem
                label={fields.raw.removalTime.label}
                name={fields.raw.removalTime.path}
                disabled={isDisabled(
                  fields.raw.removalTime.isRawField,
                  props.dataView,
                  props.showRaw
                )}
                hidden={isHidden(
                  fields.raw.removalTime.isRawField,
                  props.dataView,
                  props.showRaw
                )}
                instrumented={true}
              />
            </FormRowWithValidation>

            <DualTimeZoneDateTimePicker
              id="WasteDataRemoval"
              label={fields.enriched.removalDate.label}
              name={fields.enriched.removalDate.path}
              imperialName={fields.enriched.removalDateUS.path}
              isImperial={props.isImperial}
              isDisabled={isDisabled(
                fields.enriched.removalDate.isRawField,
                props.dataView,
                props.showRaw
              )}
              isHidden={isHidden(
                fields.enriched.removalDate.isRawField,
                props.dataView,
                props.showRaw
              )}
              form={form}
              firstInputRef={props.dataView !== RAW ? firstInputRef : null}
              validateDate={validateArrivalDate}
              warningMessages={removalDateWarnings}
              instrumented={true}
            />

            <FormRowWithValidation>
              <FormItem
                label={fields.raw.removalExitDate.label}
                name={fields.raw.removalExitDate.path}
                disabled={isDisabled(
                  fields.raw.removalExitDate.isRawField,
                  props.dataView,
                  props.showRaw
                )}
                hidden={isHidden(
                  fields.raw.removalExitDate.isRawField,
                  props.dataView,
                  props.showRaw
                )}
                instrumented={true}
              />

              <FormItem
                label={fields.raw.removalExitTime.label}
                name={fields.raw.removalExitTime.path}
                disabled={isDisabled(
                  fields.raw.removalExitTime.isRawField,
                  props.dataView,
                  props.showRaw
                )}
                hidden={isHidden(
                  fields.raw.removalExitTime.isRawField,
                  props.dataView,
                  props.showRaw
                )}
                instrumented={true}
              />
            </FormRowWithValidation>

            <DualTimeZoneDateTimePicker
              id="WasteDataRemovalExit"
              label={fields.enriched.removalExitDate.label}
              name={fields.enriched.removalExitDate.path}
              imperialName={fields.enriched.removalExitDateUS.path}
              isImperial={props.isImperial}
              isDisabled={isDisabled(
                fields.enriched.removalExitDate.isRawField,
                props.dataView,
                props.showRaw
              )}
              isHidden={isHidden(
                fields.enriched.removalExitDate.isRawField,
                props.dataView,
                props.showRaw
              )}
              form={form}
              validateDate={validateExitDate}
              warningMessages={removalExitDateWarnings}
              onFocus={() => {
                exitDatePopulation.onFocus()
                form.trigger(fields.enriched.removalExitDate.path)
              }}
              instrumented={true}
            />

            <FormItem
              label={fields.raw.consignmentId.label}
              name={fields.raw.consignmentId.path}
              disabled={isDisabled(
                fields.raw.consignmentId.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.raw.consignmentId.isRawField,
                props.dataView,
                props.showRaw
              )}
              instrumented={true}
            />
            <FormItem
              label={fields.enriched.consignmentId.label}
              name={fields.enriched.consignmentId.path}
              id="wasteDataConsignmentId"
              disabled={isDisabled(
                fields.enriched.consignmentId.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.enriched.consignmentId.isRawField,
                props.dataView,
                props.showRaw
              )}
              rules={
                isHidden(
                  fields.enriched.consignmentId.isRawField,
                  props.dataView,
                  props.showRaw
                )
                  ? null
                  : Rules.MandatoryString
              }
              simpleDataValidationRules={CheckConsignmentOrDeliveryNumber}
              instrumented={true}
            />

            <FormSpace />

            <FormItem
              label={fields.raw.subcontractorName.label}
              name={fields.raw.subcontractorName.path}
              disabled={isDisabled(
                fields.raw.subcontractorName.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.raw.subcontractorName.isRawField,
                props.dataView,
                props.showRaw
              )}
              instrumented={true}
            />
            <AutoCompleteFormItem
              label={fields.enriched.subcontractorName.label}
              name={fields.enriched.subcontractorName.path}
              id="wasteDataSubContractorName"
              disabled={isDisabled(
                fields.enriched.subcontractorName.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.enriched.subcontractorName.isRawField,
                props.dataView,
                props.showRaw
              )}
              simpleDataValidationRules={(v: string | null) =>
                CheckSubContractor(v, props.projectName)
              }
              instrumented={true}
            />

            <FormItem
              label={fields.raw.originSiteAddress.label}
              name={fields.raw.originSiteAddress.path}
              disabled={isDisabled(
                fields.raw.originSiteAddress.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.raw.originSiteAddress.isRawField,
                props.dataView,
                props.showRaw
              )}
              instrumented={true}
            >
              <Input.TextArea autoSize rows={1} />
            </FormItem>
            <FormItem
              label={fields.enriched.originSiteAddress.label}
              name={fields.enriched.originSiteAddress.path}
              id="wasteDataOriginSiteAddress"
              disabled={isDisabled(
                fields.enriched.originSiteAddress.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={true}
            >
              <Input.TextArea autoSize rows={1} />
            </FormItem>

            {journeyDistanceMiles > 1000 && (
              <p className="validation-warning">
                Calculated journey distance exceeds recommended limit. Please
                confirm addresses are correct.
              </p>
            )}

            <KnownAddressInput
              form={form}
              addressName="Origin Site"
              knownlabel={fields.enriched.originSiteAddressKnown.label}
              knownPropertyPath={fields.enriched.originSiteAddressKnown.path}
              givenPropertyPath={fields.enriched.originSiteAddress.path}
              enteredPropertyPath={
                fields.enriched.originSiteAddressEntered.path
              }
              coordinatesPropertyPath={
                fields.enriched.originSiteAddressCoordinates.path
              }
              disabled={isDisabled(
                fields.enriched.originSiteAddressKnown.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.enriched.originSiteAddressKnown.isRawField,
                props.dataView,
                props.showRaw
              )}
              projectCountry={props.projectCountry}
              instrumented={true}
            />

            <FormItem
              label={fields.raw.classificationCode.label}
              name={fields.raw.classificationCode.path}
              disabled={isDisabled(
                fields.raw.classificationCode.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.raw.classificationCode.isRawField,
                props.dataView,
                props.showRaw
              )}
              instrumented={true}
            />
            <AutoCompleteFormItem
              label={fields.enriched.classificationCode.label}
              name={fields.enriched.classificationCode.path}
              id="wasteDataSICCode"
              disabled={isDisabled(
                fields.enriched.classificationCode.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.enriched.classificationCode.isRawField,
                props.dataView,
                props.showRaw
              )}
              simpleDataValidationRules={CheckWasteSICCode}
              instrumented={true}
            />

            {!isHidden(
              fields.enriched.classificationCode.isRawField,
              props.dataView,
              props.showRaw
            ) && <FormSpace />}

            <FormItem
              label={fields.raw.vehicleRegistration.label}
              name={fields.raw.vehicleRegistration.path}
              disabled={isDisabled(
                fields.raw.vehicleRegistration.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.raw.vehicleRegistration.isRawField,
                props.dataView,
                props.showRaw
              )}
              uppercaseInput={true}
              instrumented={true}
            />
            <AutoCompleteFormItem
              label={fields.enriched.vehicleRegistration.label}
              name={fields.enriched.vehicleRegistration.path}
              id="wasteDataVehicleReg"
              disabled={isDisabled(
                fields.enriched.vehicleRegistration.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.enriched.vehicleRegistration.isRawField,
                props.dataView,
                props.showRaw
              )}
              simpleDataValidationRules={CheckVehicleReg}
              uppercaseInput={true}
              instrumented={true}
            />
          </>
        )}
      </UnstyledSection>
      <UnstyledSection {...props}>
        {({ firstInputRef }) => (
          <>
            <FormSpace />
            <hr />
            <FormSpace />

            {removalDate && (
              <CarrierPresetSelect
                onChange={handleCarrierSelectChange}
                disabled={isDisabled(
                  fields.enriched.carrierName.isRawField,
                  props.dataView,
                  props.showRaw
                )}
                hidden={isHidden(
                  fields.enriched.carrierName.isRawField,
                  props.dataView,
                  props.showRaw
                )}
              />
            )}
            {!removalDate && props.dataView !== RAW && (
              <p className="warning">
                To enable the Carrier selector please enter a value for{' '}
                {fields.enriched.removalDate.label}
              </p>
            )}

            {!isHidden(
              fields.enriched.carrierName.isRawField,
              props.dataView,
              props.showRaw
            ) && <FormSpace />}

            <FormItem
              label={fields.raw.carrierName.label}
              name={fields.raw.carrierName.path}
              disabled={isDisabled(
                fields.raw.carrierName.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.raw.carrierName.isRawField,
                props.dataView,
                props.showRaw
              )}
              inputRef={props.dataView === RAW ? firstInputRef : null}
              instrumented={true}
            />
            <AutoCompleteFormItem
              label={fields.enriched.carrierName.label}
              name={fields.enriched.carrierName.path}
              id="wasteDataCarrierName"
              disabled={isDisabled(
                fields.enriched.carrierName.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.enriched.carrierName.isRawField,
                props.dataView,
                props.showRaw
              )}
              inputRef={props.dataView !== RAW ? firstInputRef : null}
              simpleDataValidationRules={CheckCarrierName}
              instrumented={true}
            />

            <FormItem
              label={fields.raw.carrierLicenceId.label}
              name={fields.raw.carrierLicenceId.path}
              disabled={isDisabled(
                fields.raw.carrierLicenceId.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.raw.carrierLicenceId.isRawField,
                props.dataView,
                props.showRaw
              )}
              instrumented={true}
            />
            <AutoCompleteFormItem
              label={fields.enriched.carrierLicenceId.label}
              name={fields.enriched.carrierLicenceId.path}
              id="wasteDataCarrierLicence"
              disabled={isDisabled(
                fields.enriched.carrierLicenceId.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.enriched.carrierLicenceId.isRawField,
                props.dataView,
                props.showRaw
              )}
              simpleDataValidationRules={CheckCarrierLicence}
              instrumented={true}
            />
          </>
        )}
      </UnstyledSection>
      <UnstyledSection {...props}>
        {({ firstInputRef }) => (
          <>
            <FormSpace />
            <hr />
            <FormSpace />
            {removalDate && (
              <FacilityPresetSelect
                setPermitPresentedTrue={undefined}
                onChange={handleFacilitySelectChange}
                disabled={isDisabled(
                  fields.enriched.facilityOperatorName.isRawField,
                  props.dataView,
                  props.showRaw
                )}
                hidden={isHidden(
                  fields.enriched.facilityOperatorName.isRawField,
                  props.dataView,
                  props.showRaw
                )}
              />
            )}
            {!removalDate && props.dataView !== RAW && (
              <p className="warning">
                To enable the Facility selector please enter a value for{' '}
                {fields.enriched.removalDate.label}
              </p>
            )}
            {props.dataView !== RAW && (
              <p
                className={enrichedSourceTracking?.type}
                style={{ marginTop: '0.5rem' }}
              >
                {enrichedSourceTracking?.message}
              </p>
            )}
            {!isHidden(
              fields.enriched.facilityOperatorName.isRawField,
              props.dataView,
              props.showRaw
            ) && <FormSpace />}
            <FormItem
              label={fields.raw.facilityOperatorName.label}
              name={fields.raw.facilityOperatorName.path}
              disabled={isDisabled(
                fields.raw.facilityOperatorName.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.raw.facilityOperatorName.isRawField,
                props.dataView,
                props.showRaw
              )}
              inputRef={props.dataView === RAW ? firstInputRef : null}
              instrumented={true}
            />
            <AutoCompleteFormItem
              label={fields.enriched.facilityOperatorName.label}
              name={fields.enriched.facilityOperatorName.path}
              id="wasteDataFacilityOperatorName"
              disabled={isDisabled(
                fields.enriched.facilityOperatorName.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.enriched.facilityOperatorName.isRawField,
                props.dataView,
                props.showRaw
              )}
              inputRef={props.dataView !== RAW ? firstInputRef : null}
              instrumented={true}
            />
            <FormItem
              label={fields.raw.facilitySiteAddress.label}
              name={fields.raw.facilitySiteAddress.path}
              disabled={isDisabled(
                fields.raw.facilitySiteAddress.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.raw.facilitySiteAddress.isRawField,
                props.dataView,
                props.showRaw
              )}
              instrumented={true}
            >
              <Input.TextArea autoSize rows={1} />
            </FormItem>
            <FormItem
              label={fields.enriched.facilitySiteAddress.label}
              name={fields.enriched.facilitySiteAddress.path}
              id="wasteDataFacilitySiteAddress"
              disabled={isDisabled(
                fields.enriched.facilitySiteAddress.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={true}
            >
              <Input.TextArea autoSize rows={1} />
            </FormItem>
            <KnownAddressInput
              form={form}
              addressName="Facility Site"
              knownlabel={fields.enriched.facilitySiteAddressKnown.label}
              knownPropertyPath={fields.enriched.facilitySiteAddressKnown.path}
              givenPropertyPath={fields.enriched.facilitySiteAddress.path}
              enteredPropertyPath={
                fields.enriched.facilitySiteAddressEntered.path
              }
              coordinatesPropertyPath={
                fields.enriched.facilitySiteAddressCoordinates.path
              }
              disabled={isDisabled(
                fields.enriched.facilitySiteAddressKnown.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.enriched.facilitySiteAddressKnown.isRawField,
                props.dataView,
                props.showRaw
              )}
              projectCountry={props.projectCountry}
              instrumented={true}
            />
            <FormItem
              label={fields.raw.facilityPermitId.label}
              name={fields.raw.facilityPermitId.path}
              disabled={isDisabled(
                fields.raw.facilityPermitId.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.raw.facilityPermitId.isRawField,
                props.dataView,
                props.showRaw
              )}
              instrumented={true}
            />
            <AutoCompleteFormItem
              label={fields.enriched.facilityPermitId.label}
              name={fields.enriched.facilityPermitId.path}
              id="wasteDataFacilityPermit"
              disabled={isDisabled(
                fields.enriched.facilityPermitId.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.enriched.facilityPermitId.isRawField,
                props.dataView,
                props.showRaw
              )}
              simpleDataValidationRules={CheckWastePermit}
              instrumented={true}
            />
          </>
        )}
      </UnstyledSection>
      <UnstyledSection {...props}>
        {({ firstInputRef }) => (
          <>
            {props.dataView === RAW && (
              <>
                <FormSpace />
                <hr />
                <FormSpace />
              </>
            )}
            <FormItem
              label={fields.raw.receiverReferenceNumber.label}
              name={fields.raw.receiverReferenceNumber.path}
              inputRef={props.dataView === RAW ? firstInputRef : null}
              disabled={isDisabled(
                fields.raw.receiverReferenceNumber.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.raw.receiverReferenceNumber.isRawField,
                props.dataView,
                props.showRaw
              )}
              instrumented={true}
            />
            <AutoCompleteFormItem
              label={fields.enriched.receiverReferenceNumber.label}
              name={fields.enriched.receiverReferenceNumber.path}
              inputRef={props.dataView !== RAW ? firstInputRef : null}
              id="wasteDataReceiverReference"
              disabled={isDisabled(
                fields.enriched.receiverReferenceNumber.isRawField,
                props.dataView,
                props.showRaw
              )}
              // Always hidden in enriched view
              hidden={true}
              simpleDataValidationRules={
                CheckReceiverOrSupplierOrSenderOrCarrierReference
              }
              instrumented={true}
            />
            <FormItem
              label={fields.raw.supplierReferenceNumber.label}
              name={fields.raw.supplierReferenceNumber.path}
              disabled={isDisabled(
                fields.raw.supplierReferenceNumber.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={isHidden(
                fields.raw.supplierReferenceNumber.isRawField,
                props.dataView,
                props.showRaw
              )}
              instrumented={true}
            />
            <AutoCompleteFormItem
              label={fields.enriched.supplierReferenceNumber.label}
              name={fields.enriched.supplierReferenceNumber.path}
              id="wasteDataSupplierReference"
              disabled={isDisabled(
                fields.enriched.supplierReferenceNumber.isRawField,
                props.dataView,
                props.showRaw
              )}
              // Always hidden in enriched view
              hidden={true}
              simpleDataValidationRules={
                CheckReceiverOrSupplierOrSenderOrCarrierReference
              }
              instrumented={true}
            />

            {/* // Notes are always hidden */}
            <FormItem
              label={fields.enriched.notes.label}
              name={fields.enriched.notes.path}
              id="wasteDataNotes"
              disabled={isDisabled(
                fields.enriched.notes.isRawField,
                props.dataView,
                props.showRaw
              )}
              hidden={true}
              instrumented={true}
            >
              <Input.TextArea autoSize rows={1} />
            </FormItem>
          </>
        )}
      </UnstyledSection>
    </UnstyledSectionWrapper>
  )
}

export const NarrowFormRowWithWarnings = styled.div`
  display: flex;
  justify-content: space-between;

  div.reduced-field {
    overflow-x: visible;
    max-width: 55.5%;
    > div {
      overflow: visible;
    }
  }

  p.validation-warning {
    width: 180%;
  }
`
